import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LuExternalLink, LuLink } from "react-icons/lu";
import toast from "react-hot-toast";

export const DetachToggleButton = ({ mode, toggleMode, className = "" }) => (
  <button
    type="button"
    className={`inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-600 transition hover:bg-gray-50 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${className}`}
    onClick={(event) => {
      event.stopPropagation();
      toggleMode();
    }}
    title={mode === "popup" ? "Rattacher l'aperçu" : "Ouvrir dans une fenêtre"}
  >
    {mode === "popup" ? <LuLink className="h-3.5 w-3.5" /> : <LuExternalLink className="h-3.5 w-3.5" />}
    {mode === "popup" ? "Attacher" : "Fenêtre"}
  </button>
);

const cloneStyles = (sourceDoc, targetDoc) => {
  try {
    const styleNodes = sourceDoc.querySelectorAll("link[rel='stylesheet'], style");
    styleNodes.forEach((node) => targetDoc.head.appendChild(node.cloneNode(true)));
  } catch (error) {
    console.warn("Unable to clone styles for detached window", error);
  }
};

const DetachablePrescriptionViewer = ({ id, popupTitle = "Prescription", onModeChange = () => {}, renderAttached, children, className }) => {
  const storageKey = id ? `detachable-viewer:${id}` : "detachable-viewer:default";
  const popupWindowRef = useRef(null);
  const [portalContainer, setPortalContainer] = useState(null);
  const [windowState, setWindowState] = useState(window.localStorage.getItem(storageKey) ? { ...JSON.parse(window.localStorage.getItem(storageKey)) } : { mode: "attach" });

  const saveConfiguration = useCallback(() => {
    if (windowState.mode !== "popup") return window.localStorage.setItem(storageKey, JSON.stringify(windowState));

    if (!popupWindowRef.current) return;

    const width = Number.isFinite(popupWindowRef.current.outerWidth) ? popupWindowRef.current.outerWidth : popupWindowRef.current.innerWidth;
    const height = Number.isFinite(popupWindowRef.current.outerHeight) ? popupWindowRef.current.outerHeight : popupWindowRef.current.innerHeight;
    const left = typeof popupWindowRef.current.screenX === "number" ? popupWindowRef.current.screenX : popupWindowRef.current.screenLeft;
    const top = typeof popupWindowRef.current.screenY === "number" ? popupWindowRef.current.screenY : popupWindowRef.current.screenTop;

    if (windowState.width === width && windowState.height === height && windowState.left === left && windowState.top === top) return;

    setWindowState({ ...windowState, width, height, left, top });
    window.localStorage.setItem(storageKey, JSON.stringify({ ...windowState, width, height, left, top }));
  }, [windowState, storageKey]);

  useEffect(() => {
    const interval = setInterval(saveConfiguration, 5000);
    return () => clearInterval(interval);
  }, [saveConfiguration]);

  const closePopupWindow = useCallback(() => {
    if (popupWindowRef.current && !popupWindowRef.current.closed) {
      popupWindowRef.current.close();
      popupWindowRef.current = null;
      setWindowState((prev) => ({ ...prev, mode: "attach" }));
    }
    setPortalContainer(null);
  }, []);

  const openPopupWindow = useCallback(async () => {
    try {
      await window.getScreenDetails();
    } catch (error) {
      console.warn("Window Management API not available or permission denied", error);
    }

    const features = ["resizable=yes", "scrollbars=yes"];
    if (typeof windowState.width === "number") features.push(`width=${Math.round(windowState.width)}`);
    if (typeof windowState.height === "number") features.push(`height=${Math.round(windowState.height)}`);
    if (typeof windowState.left === "number") features.push(`left=${Math.round(windowState.left)}`);
    if (typeof windowState.top === "number") features.push(`top=${Math.round(windowState.top)}`);

    let externalWindow = window.open("", "_querco_admission_viewer", features.join(","));
    if (!externalWindow) externalWindow = window.open("", "_querco_admission_viewer");
    if (!externalWindow) {
      setWindowState((prev) => ({ ...prev, mode: "attach" }));
      return toast.error("Impossible d'ouvrir la fenêtre. Veuillez autoriser les popups.");
    }

    externalWindow.document.body.innerHTML = "";
    externalWindow.document.body.style.margin = "0";
    externalWindow.document.title = popupTitle;
    cloneStyles(document, externalWindow.document);

    const container = externalWindow.document.createElement("div");
    externalWindow.document.body.appendChild(container);
    setPortalContainer(container);

    popupWindowRef.current = externalWindow;

    externalWindow.addEventListener("beforeunload", closePopupWindow);
    return () => externalWindow.removeEventListener("beforeunload", closePopupWindow);
  }, [windowState]);

  useEffect(() => {
    window.addEventListener("beforeunload", closePopupWindow);

    return () => {
      window.removeEventListener("beforeunload", closePopupWindow);
      closePopupWindow();
    };
  }, []);

  useEffect(() => {
    onModeChange(windowState.mode);

    if (windowState.mode === "popup") {
      // Defer window.open() to avoid React scheduler conflicts
      // window.open() can trigger synchronous events that cause state updates
      // while React is still in its commit phase
      const timeoutId = setTimeout(openPopupWindow, 0);
      return () => clearTimeout(timeoutId);
    }

    closePopupWindow();
  }, [windowState.mode]);

  useEffect(() => {
    if (popupWindowRef.current) popupWindowRef.current.document.title = popupTitle;
  }, [popupTitle]);

  const toggleMode = useCallback(() => {
    setWindowState((prev) => ({ ...prev, mode: prev.mode === "popup" ? "attach" : "popup" }));
  }, []);

  const childContent = typeof children === "function" ? children({ mode: windowState.mode, toggleMode }) : children;

  const viewerContent = (
    <div className={`relative w-full h-full ${className || ""}`} key={windowState.mode === "popup" ? "popup" : "attach"}>
      {childContent}
    </div>
  );

  const renderProps = { viewer: viewerContent, mode: windowState.mode };
  const attachedContent = typeof renderAttached === "function" ? renderAttached(renderProps) : viewerContent;

  return (
    <>
      {windowState.mode === "popup" && portalContainer && createPortal(viewerContent, portalContainer)}
      {windowState.mode !== "popup" && attachedContent}
    </>
  );
};

export default DetachablePrescriptionViewer;
